import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  AppBar,
  Toolbar,
  Container,
  Paper,
  alpha,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Description as FileText,
  Refresh as RefreshCw,
  Public as Globe,
  Edit,
  Check,
  TrendingUp,
  Speed,
  Translate,
  FormatQuote,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import GavelIcon from "@mui/icons-material/Gavel";

const toolsData = [
  {
    id: "Summarizer",
    icon: FileText,
    color: "#3B82F6",
    gradientStart: "#3B82F6",
    gradientEnd: "#1E40AF",
    title: "AI Summarizer",
    description: "Turn long texts into clear, impactful summaries",
    features: [
      "Extract key points automatically",
      "Adjustable summary length",
      "Multiple document formats",
    ],
    badge: "Fast",
    badgeColor: "#3B82F6",
    fontSize: 16,
    buttonText: "Summarize",
    buttonIcon: TrendingUp,
  },
  {
    id: "paraphrase",
    icon: RefreshCw,
    color: "#10B981",
    gradientStart: "#10B981",
    gradientEnd: "#047857",
    title: "AI Paraphraser",
    description: "Transform text for clarity and originality.",
    features: [
      "Multiple writing styles",
      "Preserve original meaning",
      "Plagiarism-free content",
    ],
    badge: "Accurate",
    fontSize: 16,
    badgeColor: "#10B981",
    buttonText: "Paraphrase",
    buttonIcon: RefreshCw,
  },
  {
    id: "translator",
    icon: Globe,
    color: "#8B5CF6",
    gradientStart: "#8B5CF6",
    gradientEnd: "#6D28D9",
    title: "AI Translator",
    description: "Break language barriers with accurate, natural translations",
    features: [
      "100+ languages supported",
      "Context-aware translation",
      "Cultural adaptation",
    ],
    badge: "Multi-language",
    fontSize: 16,
    badgeColor: "#8B5CF6",
    buttonText: "Translate",
    buttonIcon: Translate,
  },

  {
    id: "plagiarism",
    icon: GavelIcon,
    color: "#e34c1fff",
    gradientStart: "#e34c1fff",
    gradientEnd: "#e34c1fff",
    title: "AI Plagiarism Checker",
    description:
      "Ensure originality with advanced AI-powered plagiarism detection",
    features: [
      "Scans across millions of web sources",
      "Identifies exact and paraphrased matches",
      "Provides detailed similarity reports",
    ],
    badge: "Originality Check",
    fontSize: 16,
    badgeColor: "#e34c1fff",
    buttonText: "Plagiarism",
    buttonIcon: GavelIcon,
  },

  {
    id: "citation",
    icon: FormatQuote,
    color: "#cb155dff",
    gradientStart: "#cb155dff",
    gradientEnd: "#cb155dff",
    title: "AI Citation Generator",
    description:
      "Generate accurate, properly formatted citations instantly with AI-powered precision",
    features: [
      "Supports major citation styles (APA, MLA, Chicago, and more)",
      "Automatically extracts and formats source details",
      "Ensures academic accuracy and consistency",
    ],
    badge: "Smart Citation",
    fontSize: 16,
    badgeColor: "#cb155dff",
    buttonText: "Citation",
    buttonIcon: FormatQuote,
  },
];

export default function MultiToolWorkspace() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const history = useHistory();
  const handleToolSelect = (toolId) => {
    console.log(`Navigating to: ${toolId}`);
    {
      history.push({
        pathname: `/${toolId}`,
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "#F8FAFC",
        backgroundImage:
          "radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.03) 0%, transparent 50%)",
      }}
    >
      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          {/* Enhanced Header */}
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "rgb(11, 79, 231);",
                mb: 2,
                letterSpacing: "-0.02em",
                fontSize: { xs: "2rem", md: "2.5rem" },
              }}
            >
              <Typography variant="h3" fontWeight="700" gutterBottom>
                🔮 Powerful AI Writing Tool
              </Typography>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "#000000ff",
                mb: 4,
                fontWeight: 400,
                fontfamily: "cursive",
                // maxWidth: 600,

                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              Transform your content with our advanced AI-powered writing
              assistant. Summarize, paraphrase, and translate with ease.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: "center",
                flexWrap: "wrap",
                paddingTop: "10px",
              }}
            >
              <Chip
                label="⚡ Lightning Fast"
                sx={{
                  bgcolor: alpha("#3B82F6", 0.1),
                  color: "#1E40AF",
                  fontWeight: 600,
                  fontSize: 16,
                  px: 1,
                  border: "1px solid",
                  borderColor: alpha("#3B82F6", 0.2),
                }}
              />
              <Chip
                label="🎯 Accurate"
                sx={{
                  bgcolor: alpha("#10B981", 0.1),
                  color: "#065F46",
                  fontWeight: 600,
                  fontSize: 16,
                  px: 1,
                  border: "1px solid",
                  borderColor: alpha("#10B981", 0.2),
                }}
              />
              <Chip
                label="🌍 45+ Languages"
                sx={{
                  bgcolor: alpha("#8B5CF6", 0.1),
                  color: "#5B21B6",
                  fontWeight: 600,
                  fontSize: 16,
                  px: 1,
                  border: "1px solid",
                  borderColor: alpha("#8B5CF6", 0.2),
                }}
              />
            </Box>
          </Box>

          {/* Enhanced Tools Grid */}
          <Grid container spacing={3}>
            {toolsData.map((tool) => {
              const Icon = tool.icon;
              const ButtonIcon = tool.buttonIcon;
              const isHovered = hoveredCard === tool.id;

              return (
                <Grid xs={12} sm={8} lg={4} key={tool.id}>
                  <Card
                    onMouseEnter={() => setHoveredCard(tool.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      overflow: "visible",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      border: "1px solid",
                      borderColor: isHovered
                        ? alpha(tool.color, 0.3)
                        : "divider",
                      boxShadow: isHovered
                        ? `0 20px 40px ${alpha(
                            tool.color,
                            0.15
                          )}, 0 0 0 1px ${alpha(tool.color, 0.1)}`
                        : "0 1px 3px rgba(0,0,0,0.05)",
                      transform: isHovered
                        ? "translateY(-8px) scale(1.02)"
                        : "translateY(0) scale(1)",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${tool.gradientStart}, ${tool.gradientEnd})`,
                        opacity: isHovered ? 1 : 0,
                        transition: "opacity 0.3s",
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      {/* Icon with gradient background */}
                      <Box
                        sx={{
                          position: "relative",
                          width: 56,
                          height: 56,
                          borderRadius: 2.5,
                          background: `linear-gradient(135deg, ${tool.gradientStart}, ${tool.gradientEnd})`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2.5,
                          boxShadow: `0 8px 24px ${alpha(tool.color, 0.3)}`,
                          transform: isHovered
                            ? "rotate(-5deg) scale(1.1)"
                            : "rotate(0deg) scale(1)",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        <Icon sx={{ fontSize: 28, color: "#FFFFFF" }} />
                      </Box>

                      {/* Badge */}
                      <Chip
                        label={tool.badge}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          bgcolor: alpha(tool.badgeColor, 0.1),
                          color: tool.badgeColor,
                          border: "1px solid",
                          borderColor: alpha(tool.badgeColor, 0.2),
                          mb: 2,
                        }}
                      />

                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          mb: 1.5,
                          color: "#111827",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {tool.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6B7280",
                          mb: 3,
                          lineHeight: 1.6,
                        }}
                      >
                        {tool.description}
                      </Typography>

                      {/* Features List */}
                      <List dense sx={{ p: 0 }}>
                        {tool.features.map((feature, idx) => (
                          <ListItem key={idx} sx={{ px: 0, py: 0.75 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: "50%",
                                  bgcolor: alpha(tool.color, 0.1),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Check
                                  sx={{ fontSize: 14, color: tool.color }}
                                />
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                fontSize: "0.875rem",
                                color: "#4B5563",
                                fontWeight: 500,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>

                    {/* Enhanced Action Button */}
                    <Box sx={{ p: 3, pt: 0 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ButtonIcon sx={{ fontSize: 20 }} />}
                        onClick={() => handleToolSelect(tool.id)}
                        sx={{
                          background: isHovered
                            ? `linear-gradient(135deg, ${tool.gradientStart}, ${tool.gradientEnd})`
                            : alpha(tool.color, 0.1),
                          color: isHovered ? "#FFFFFF" : tool.color,
                          fontWeight: 600,
                          py: 1.5,
                          textTransform: "none",
                          fontSize: "1rem",
                          boxShadow: isHovered
                            ? `0 8px 20px ${alpha(tool.color, 0.3)}`
                            : "none",
                          border: isHovered
                            ? "none"
                            : `2px solid ${alpha(tool.color, 0.2)}`,
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            background: `linear-gradient(135deg, ${tool.gradientStart}, ${tool.gradientEnd})`,
                            transform: "scale(1.02)",
                            boxShadow: `0 12px 28px ${alpha(tool.color, 0.4)}`,
                          },
                        }}
                      >
                        {tool.buttonText}
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Bottom CTA Section */}
          <Paper
            elevation={0}
            sx={{
              mt: 6,
              p: 4,
              textAlign: "center",
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 1, color: "#111827" }}
            >
              🤖 Ready to transform your writing?
            </Typography>
            <Typography variant="body1" sx={{ color: "#6B7280", mb: 3 }}>
              Join thousands of users who trust our AI-powered tools for their
              content needs.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#000000ff",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Check sx={{ fontSize: 16, color: "#10B981" }} /> Free to
                start-T&C
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#000000ff",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Check sx={{ fontSize: 16, color: "#10B981" }} /> MYPOET360
                Enhanced Security
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#000000ff",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Check sx={{ fontSize: 16, color: "#10B981" }} /> End-to-End
                Encryption
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
